import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "next-themes";
import { lazy, Suspense } from "react";
import CookieBanner from "./components/CookieBanner";
import AccessibilityToolbar from "./components/AccessibilityToolbar";
import { FeatureGate } from "./components/FeatureGate";

// Critical route - loaded immediately
import Index from "./pages/Index";

// Lazy loaded routes - only loaded when needed
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const Approach = lazy(() => import("./pages/Approach"));
const Services = lazy(() => import("./pages/Services"));
const QuickScanRequest = lazy(() => import("./pages/QuickScanRequest"));
const BookingRequest = lazy(() => import("./pages/BookingRequest"));
const AdminInvoices = lazy(() => import("./pages/AdminInvoices"));
const AdminInvoiceSettings = lazy(() => import("./pages/AdminInvoiceSettings"));
const AdminInvoiceEditor = lazy(() => import("./pages/AdminInvoiceEditor"));
const AdminAuth = lazy(() => import("./pages/AdminAuth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DigitalAccessibility = lazy(() => import("./pages/knowledge/DigitalAccessibility"));
const Disabilities = lazy(() => import("./pages/knowledge/Disabilities"));
const WCAG = lazy(() => import("./pages/knowledge/WCAG"));
const WDO = lazy(() => import("./pages/knowledge/WDO"));
const ElearningWCAG = lazy(() => import("./pages/ElearningWCAG"));
const CustomerAuth = lazy(() => import("./pages/CustomerAuth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AccessibilityMonitor = lazy(() => import("./pages/AccessibilityMonitor"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="equalaccess-theme">
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <CookieBanner />
            <AccessibilityToolbar />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/accessibility" element={<Accessibility />} />
                <Route path="/approach" element={<Approach />} />
                <Route path="/werkwijze" element={<Approach />} />
                <Route path="/quickscan-aanvragen" element={<QuickScanRequest />} />
                <Route path="/belafspraak-maken" element={<BookingRequest />} />
                <Route path="/kennisbank/digitale-toegankelijkheid" element={<DigitalAccessibility />} />
                <Route path="/kennisbank/soorten-beperkingen" element={<Disabilities />} />
                <Route path="/kennisbank/wcag" element={<WCAG />} />
                <Route path="/kennisbank/wet-digitale-overheid" element={<WDO />} />
                <Route 
                  path="/services/elearning-wcag" 
                  element={
                    <FeatureGate featureKey="elearning_wcag">
                      <ElearningWCAG />
                    </FeatureGate>
                  } 
                />
                <Route path="/admin/invoices" element={<AdminInvoices />} />
                <Route path="/admin/invoice-settings" element={<AdminInvoiceSettings />} />
                <Route path="/admin/invoice-editor" element={<AdminInvoiceEditor />} />
                <Route path="/admin/auth" element={<AdminAuth />} />
                <Route path="/auth" element={<CustomerAuth />} />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/toegankelijkheidsmonitor" element={<AccessibilityMonitor />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
